import { z } from 'zod';
import { slugSchema } from './tenancy.js';
import { schedulingWeightsSchema } from './motor.js';

export const MINISTRY_ROLE = ['LEADER', 'ASSISTANT', 'MEMBER'] as const;
export const QUALIFICATION_LEVEL = ['APPRENTICE', 'QUALIFIED', 'REFERENCE'] as const;
export const FUNCTION_PREFERENCE = ['LIKES', 'AVOIDS'] as const;

export const ministryRoleSchema = z.enum(MINISTRY_ROLE);
export const qualificationLevelSchema = z.enum(QUALIFICATION_LEVEL);
export const functionPreferenceSchema = z.enum(FUNCTION_PREFERENCE);

export type MinistryRole = z.infer<typeof ministryRoleSchema>;
export type QualificationLevel = z.infer<typeof qualificationLevelSchema>;
export type FunctionPreference = z.infer<typeof functionPreferenceSchema>;

export const MINISTRY_ROLE_LABEL: Record<MinistryRole, string> = {
  LEADER: 'Líder',
  ASSISTANT: 'Auxiliar',
  MEMBER: 'Integrante',
};

export const QUALIFICATION_LEVEL_LABEL: Record<QualificationLevel, string> = {
  APPRENTICE: 'Aprendiz',
  QUALIFIED: 'Apto',
  REFERENCE: 'Referência',
};

export const QUALIFICATION_LEVEL_HELP: Record<QualificationLevel, string> = {
  APPRENTICE: 'Está aprendendo. Só serve acompanhado de uma referência.',
  QUALIFIED: 'Pode ser escalado normalmente nesta função.',
  REFERENCE: 'Domina a função e pode acompanhar aprendizes.',
};

const hexColor = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Informe a cor em hexadecimal, ex.: #4F46E5');

export const createMinistrySchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome do departamento').max(120),
  slug: slugSchema.optional(),
  description: z.string().trim().max(500).nullish(),
  color: hexColor.default('#4F46E5'),
  icon: z.string().trim().max(40).default('users'),
  congregationId: z.string().uuid().nullish(),
  requiresApproval: z.boolean().default(false),
});
export type CreateMinistryInput = z.infer<typeof createMinistrySchema>;

export const updateMinistrySchema = createMinistrySchema.partial().extend({
  active: z.boolean().optional(),
});
export type UpdateMinistryInput = z.infer<typeof updateMinistrySchema>;

export const ministrySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  color: z.string(),
  icon: z.string(),
  requiresApproval: z.boolean(),
  active: z.boolean(),
  congregation: z.object({ id: z.string(), name: z.string() }).nullable(),
  membersCount: z.number().int(),
  functionsCount: z.number().int(),
  leaders: z.array(z.object({ id: z.string(), name: z.string() })),
  /**
   * Pesos do motor de escala (M07). Nulo = os padrões do produto; parcial =
   * o departamento mexeu só no que lhe interessa.
   */
  schedulingWeights: schedulingWeightsSchema.partial().nullable(),
});
export type Ministry = z.infer<typeof ministrySchema>;

export const createFunctionSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome da função').max(120),
  slug: slugSchema.optional(),
  description: z.string().trim().max(500).nullish(),
  color: hexColor.nullish(),
  defaultSlots: z.coerce.number().int().min(1, 'Pelo menos uma vaga').max(50).default(1),
  requiresConfirmation: z.boolean().default(true),
  requiresApproval: z.boolean().default(false),
  allowsGuest: z.boolean().default(false),
  allowsApprentice: z.boolean().default(false),
  requiresClearance: z.boolean().default(false),
});
export type CreateFunctionInput = z.infer<typeof createFunctionSchema>;

export const updateFunctionSchema = createFunctionSchema.partial().extend({
  active: z.boolean().optional(),
});
export type UpdateFunctionInput = z.infer<typeof updateFunctionSchema>;

export const ministryFunctionSchema = z.object({
  id: z.string().uuid(),
  ministryId: z.string().uuid(),
  ministryName: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  color: z.string().nullable(),
  defaultSlots: z.number().int(),
  requiresConfirmation: z.boolean(),
  requiresApproval: z.boolean(),
  allowsGuest: z.boolean(),
  allowsApprentice: z.boolean(),
  requiresClearance: z.boolean(),
  active: z.boolean(),
  qualifiedCount: z.number().int(),
});
export type MinistryFunction = z.infer<typeof ministryFunctionSchema>;

export const addMinistryMemberSchema = z.object({
  personId: z.string().uuid('Escolha uma pessoa'),
  role: ministryRoleSchema.default('MEMBER'),
});
export type AddMinistryMemberInput = z.infer<typeof addMinistryMemberSchema>;

export const ministryMemberSchema = z.object({
  id: z.string().uuid(),
  personId: z.string().uuid(),
  personName: z.string(),
  displayName: z.string().nullable(),
  photoUrl: z.string().nullable(),
  role: ministryRoleSchema,
  joinedAt: z.string().datetime(),
});
export type MinistryMember = z.infer<typeof ministryMemberSchema>;

export const upsertQualificationSchema = z.object({
  personId: z.string().uuid('Escolha uma pessoa'),
  functionId: z.string().uuid('Escolha uma função'),
  level: qualificationLevelSchema.default('QUALIFIED'),
  /** Habilitação com prazo: vencida, a pessoa deixa de ser elegível. */
  validUntil: z.string().datetime().nullish(),
  notes: z.string().trim().max(400).nullish(),
});
export type UpsertQualificationInput = z.infer<typeof upsertQualificationSchema>;

export const qualificationSchema = z.object({
  id: z.string().uuid(),
  personId: z.string().uuid(),
  personName: z.string(),
  functionId: z.string().uuid(),
  functionName: z.string(),
  ministryName: z.string(),
  level: qualificationLevelSchema,
  qualifiedAt: z.string().datetime(),
  validUntil: z.string().datetime().nullable(),
  expired: z.boolean(),
  notes: z.string().nullable(),
});
export type Qualification = z.infer<typeof qualificationSchema>;

export const setPreferenceSchema = z.object({
  functionId: z.string().uuid(),
  preference: functionPreferenceSchema.nullable(),
});

export const PAIR_RULE_KIND = ['KEEP_TOGETHER', 'KEEP_APART'] as const;
export const pairRuleKindSchema = z.enum(PAIR_RULE_KIND);
export type PairRuleKind = z.infer<typeof pairRuleKindSchema>;

export const PAIR_RULE_LABEL: Record<PairRuleKind, string> = {
  KEEP_TOGETHER: 'Escalar sempre juntos',
  KEEP_APART: 'Não escalar juntos',
};

export const createPairRuleSchema = z
  .object({
    personAId: z.string().uuid('Escolha a primeira pessoa'),
    personBId: z.string().uuid('Escolha a segunda pessoa'),
    kind: pairRuleKindSchema,
    reason: z.string().trim().max(300).nullish(),
    ministryId: z.string().uuid().nullish(),
  })
  .refine((dados) => dados.personAId !== dados.personBId, {
    path: ['personBId'],
    message: 'Escolha duas pessoas diferentes',
  });
export type CreatePairRuleInput = z.infer<typeof createPairRuleSchema>;

/**
 * Estrutura pronta por denominação (M01, passo 2 do onboarding). O tenant
 * aplica, renomeia o que não bate com a realidade dele e segue.
 */
export interface MinistryPreset {
  name: string;
  color: string;
  icon: string;
  functions: ReadonlyArray<{ name: string; slots?: number; clearance?: boolean; apprentice?: boolean }>;
}

export const MINISTRY_PRESETS: readonly MinistryPreset[] = [
  {
    name: 'Louvor e adoração',
    color: '#7C3AED',
    icon: 'music',
    functions: [
      { name: 'Ministro de louvor' },
      { name: 'Vocal principal' },
      { name: 'Back vocal', slots: 3, apprentice: true },
      { name: 'Teclado' },
      { name: 'Violão' },
      { name: 'Guitarra' },
      { name: 'Baixo' },
      { name: 'Bateria' },
      { name: 'Cajon' },
    ],
  },
  {
    name: 'Mídia e tecnologia',
    color: '#0EA5E9',
    icon: 'video',
    functions: [
      { name: 'Operador de som (PA)' },
      { name: 'Operador de monitor' },
      { name: 'Projeção e slides', apprentice: true },
      { name: 'Iluminação' },
      { name: 'Câmera', slots: 2, apprentice: true },
      { name: 'Corte e transmissão' },
      { name: 'Fotografia' },
    ],
  },
  {
    name: 'Recepção e ordem',
    color: '#059669',
    icon: 'handshake',
    functions: [
      { name: 'Recepção da porta', slots: 2 },
      { name: 'Ordem e acomodação', slots: 2 },
      { name: 'Ofertório', slots: 4 },
      { name: 'Santa Ceia', slots: 6 },
      { name: 'Estacionamento', slots: 2 },
    ],
  },
  {
    name: 'Ministério infantil',
    color: '#F59E0B',
    icon: 'baby',
    functions: [
      { name: 'Professor do berçário', slots: 3, clearance: true },
      { name: 'Professor do maternal', slots: 2, clearance: true },
      { name: 'Professor de juniores', slots: 2, clearance: true },
      { name: 'Recepção do kids', slots: 2, clearance: true },
      { name: 'Auxiliar de sala', slots: 3, clearance: true, apprentice: true },
    ],
  },
  {
    name: 'Celebração',
    color: '#DC2626',
    icon: 'mic',
    functions: [
      { name: 'Dirigente do culto' },
      { name: 'Pregador' },
      { name: 'Avisos' },
      { name: 'Oração inicial' },
    ],
  },
  {
    name: 'Intercessão',
    color: '#4F46E5',
    icon: 'heart',
    functions: [
      { name: 'Intercessor de plantão', slots: 2 },
      { name: 'Ministração no apelo', slots: 4 },
    ],
  },
  {
    name: 'Apoio e limpeza',
    color: '#667085',
    icon: 'brush',
    functions: [
      { name: 'Limpeza pré-culto', slots: 3 },
      { name: 'Limpeza pós-culto', slots: 3 },
      { name: 'Cozinha e cantina', slots: 2 },
      { name: 'Manutenção' },
    ],
  },
];
